import type { CandidateGroup, CommandPaletteSource } from "./types";

/**
 * CommandPaletteEngine — registers pluggable sources, fans queries out to all of
 * them in parallel, and produces a grouped, score-sorted candidate list for the UI.
 */
export class CommandPaletteEngine {
    private sources = new Map<string, CommandPaletteSource>();
    private listeners = new Set<(groups: CandidateGroup[]) => void>();
    private abort: AbortController | null = null;

    /** Register a source. Overwrites any existing source with the same key. */
    registerSource(s: CommandPaletteSource): void {
        this.sources.set(s.key, s);
    }

    /** Unregister a source by key. No-op if the key is unknown. */
    unregisterSource(key: string): void {
        this.sources.delete(key);
    }

    /** Return sources in their registration order. */
    getSources(): CommandPaletteSource[] {
        return Array.from(this.sources.values());
    }

    /** Subscribe to grouped results. Returns an unsubscribe function. */
    subscribe(listener: (groups: CandidateGroup[]) => void): () => void {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }

    /**
     * Run the query against the enabled sources in parallel. Routing:
     *
     *   - Empty (trimmed) query → every enabled source is called with "".
     *     Each source decides what (if anything) to emit for the empty
     *     query — typically a small "landing page" set or nothing.
     *   - Query starts with any REGISTERED source's `prefix` (e.g. "@") →
     *     ONLY sources with that exact prefix are called, with the leading
     *     prefix character stripped before being passed to `source.search`.
     *   - Query starts with "/" (a reserved navigation prefix that no
     *     source registers, by convention) → all sources without a prefix
     *     are called with the leading "/" stripped, EXCEPT the "quick"
     *     source — slash mode is for navigation only, never landing
     *     actions.
     *   - Otherwise (non-prefix, non-empty query) → all sources WITHOUT a
     *     prefix are called with the full query. Prefix-only sources stay
     *     silent.
     *
     * The previous in-flight search (if any) is aborted before this one
     * starts. Returns the grouped result and also notifies subscribers.
     */
    async search(query: string): Promise<CandidateGroup[]> {
        if (this.abort) {
            this.abort.abort();
        }
        const controller = new AbortController();
        this.abort = controller;
        const signal = controller.signal;

        const allEnabled = this.getSources().filter((source) => {
            const enabled = source.enabled;
            if (enabled === undefined) return true;
            if (typeof enabled === "function") return enabled();
            return enabled;
        });

        // Decide which sources to fan out to + what query string each one sees.
        const trimmed = query.trim();
        let routed: { source: CommandPaletteSource; query: string }[];
        if (trimmed.length === 0) {
            // Empty query: only the "quick" landing source is called. The
            // other general sources stay silent so opening the palette
            // doesn't dump the full app / menu / action catalogue onto the
            // user. (Apps + menus + actions reappear as soon as the user
            // types anything, or hits "/" for explicit navigation mode.)
            routed = allEnabled
                .filter((s) => s.key === "quick")
                .map((source) => ({ source, query: "" }));
        } else {
            const firstChar = trimmed.charAt(0);
            const prefixed = allEnabled.filter((s) => s.prefix && s.prefix === firstChar);
            if (prefixed.length > 0) {
                // Registered-prefix mode: only the matching prefix-source(s),
                // with the prefix stripped.
                const stripped = trimmed.slice(1).trimStart();
                routed = prefixed.map((source) => ({ source, query: stripped }));
            } else if (firstChar === "/") {
                // Reserved menu/navigation prefix: route to non-prefix
                // sources WITHOUT the "quick" landing source. The leading
                // "/" is stripped so menu/app fuzzy matchers see clean text.
                const stripped = trimmed.slice(1).trimStart();
                routed = allEnabled
                    .filter((s) => !s.prefix && s.key !== "quick")
                    .map((source) => ({ source, query: stripped }));
            } else {
                // General mode: only sources with no prefix.
                routed = allEnabled
                    .filter((s) => !s.prefix)
                    .map((source) => ({ source, query: trimmed }));
            }
        }

        const settled = await Promise.allSettled(
            routed.map(async ({ source, query: routedQuery }) => {
                const result = source.search(routedQuery, signal);
                const items = await Promise.resolve(result);
                return { source, items };
            }),
        );

        const groups: CandidateGroup[] = [];
        for (let i = 0; i < settled.length; i += 1) {
            const outcome = settled[i];
            if (outcome.status !== "fulfilled") continue;
            const { source, items } = outcome.value;
            const filtered = items
                .filter((candidate) => candidate.score != null && candidate.score !== 0)
                .sort((a, b) => b.score - a.score);
            if (filtered.length === 0) continue;
            groups.push({ key: source.key, label: source.label, items: filtered });
        }

        // Only notify if this search hasn't been superseded.
        if (!signal.aborted) {
            for (const listener of this.listeners) {
                listener(groups);
            }
        }

        return groups;
    }

    /** Abort any in-flight search and drop all listeners. */
    dispose(): void {
        if (this.abort) {
            this.abort.abort();
            this.abort = null;
        }
        this.listeners.clear();
    }
}
